import {
    ServerDiagnostics,
    SqlDiagnostics,
    DiagnosticsStatus,
    DriveDiagnostics,
    DatabaseDiagnostics
} from "../../Models/Admin/Diagnostics";

export class DiagnosticsManager {

    private diagnosticsTabContainer!: JQuery;
    private tableStorageTabContainer!: JQuery;

    private serverDiagnostics!: ServerDiagnostics;
    private sqlDiagnostics!: SqlDiagnostics;

    private readonly diagnosticIdQueryParameter = 'diagnosticId';

    constructor(private containerElement: JQuery) { }

    public initialize(): void {
        try {
            this.initializeContainers();
            this.loadDiagnosticsData();
            this.initializeDiagnosticHistory();
            this.renderDiagnostics();
            this.initializeEvents();
        }
        catch (error) {
            console.error('Unable to initialize DiagnosticsManager.', error);
        }
    }

    private initializeContainers(): void {
        this.diagnosticsTabContainer = this.containerElement.find('#diagnostics-tab');
        this.tableStorageTabContainer = this.containerElement.find('#table-storage-tab');
    }

    private loadDiagnosticsData(): void {
        const serverJson = this.diagnosticsTabContainer.find('#serverDiagnosticsJson').val() as string;
        const sqlJson = this.diagnosticsTabContainer.find('#sqlDiagnosticsJson').val() as string;

        if (!serverJson) {
            throw new Error('Server diagnostics JSON is empty.');
        }

        if (!sqlJson) {
            throw new Error('SQL diagnostics JSON is empty.');
        }

        this.serverDiagnostics = JSON.parse(serverJson) as ServerDiagnostics;
        this.sqlDiagnostics = JSON.parse(sqlJson) as SqlDiagnostics;
    }

    private renderDiagnostics(): void {
        this.renderHeader();
        this.renderCpu();
        this.renderMemory();
        this.renderStorage();
        this.renderDatabases();
        this.renderSystemInformation();
        this.renderOverallStatus();
        this.renderTechnicalDetails();
    }

    private initializeEvents(): void {
        this.diagnosticsTabContainer
            .find('#toggleTechnicalDetails')
            .off('click.diagnostics')
            .on('click.diagnostics', () => this.toggleTechnicalDetails());

        this.initializeDiagnosticHistoryEvents();
    }

    // -------------------------------------------------------------------------
    // Diagnostic history
    // -------------------------------------------------------------------------

    private initializeDiagnosticHistory(): void {
        const items = this.getDiagnosticHistoryItems();

        if (!items.length) {
            return;
        }

        /*
         * If a diagnosticId is present in the URL, make sure the matching
         * history item is marked as active.
         */
        const selectedId = new URL(window.location.href)
            .searchParams
            .get(this.diagnosticIdQueryParameter);

        if (selectedId) {
            const matchingItem = items.filter((_index, element) =>
                this.getDiagnosticId($(element)) === selectedId
            ).first();

            if (matchingItem.length) {
                items.removeClass('is-active');
                matchingItem.addClass('is-active');
            }
        }

        /*
         * If the server didn't mark an item as active, select the newest one.
         */
        if (!items.filter('.is-active').length) {
            items.first().addClass('is-active');
        }

        this.updateSelectedDiagnosticText();
        this.updateDiagnosticNavigation();
    }

    private initializeDiagnosticHistoryEvents(): void {
        const dropdown = this.diagnosticsTabContainer.find('#diagnosticsHistoryDropdown');

        if (!dropdown.length) {
            return;
        }

        this.diagnosticsTabContainer
            .find('#selectedDiagnosticButton')
            .off('click.diagnosticsHistory')
            .on('click.diagnosticsHistory', event => {
                event.stopPropagation();
                this.toggleDiagnosticHistory();
            });

        this.diagnosticsTabContainer
            .find('#diagnosticsHistoryMenu')
            .off('click.diagnosticsHistory')
            .on('click.diagnosticsHistory', event => event.stopPropagation());

        this.diagnosticsTabContainer
            .find('#diagnosticSearchInput')
            .off('input.diagnosticsHistory')
            .on('input.diagnosticsHistory', () => this.filterDiagnosticHistory());

        this.diagnosticsTabContainer
            .find('#diagnosticHistoryList')
            .off('click.diagnosticsHistory', '.diagnostics-history-item')
            .on('click.diagnosticsHistory', '.diagnostics-history-item', event => {
                this.selectDiagnostic($(event.currentTarget));
            });

        this.diagnosticsTabContainer
            .find('#previousDiagnosticButton')
            .off('click.diagnosticsHistory')
            .on('click.diagnosticsHistory', () => this.navigateRelativeDiagnostic(1));

        this.diagnosticsTabContainer
            .find('#nextDiagnosticButton')
            .off('click.diagnosticsHistory')
            .on('click.diagnosticsHistory', () => this.navigateRelativeDiagnostic(-1));

        $(document)
            .off('click.diagnosticsHistory')
            .on('click.diagnosticsHistory', () => this.closeDiagnosticHistory());

        $(document)
            .off('keydown.diagnosticsHistory')
            .on('keydown.diagnosticsHistory', event => {
                if (event.key === 'Escape') {
                    this.closeDiagnosticHistory();
                }
            });
    }

    private toggleDiagnosticHistory(): void {
        const menu = this.diagnosticsTabContainer.find('#diagnosticsHistoryMenu');
        const button = this.diagnosticsTabContainer.find('#selectedDiagnosticButton');

        if (menu.hasClass('d-none')) {
            menu.removeClass('d-none');
            button.addClass('is-open');

            setTimeout(() => {
                this.diagnosticsTabContainer.find('#diagnosticSearchInput').trigger('focus');
            });
        }
        else {
            this.closeDiagnosticHistory();
        }
    }

    private closeDiagnosticHistory(): void {
        this.diagnosticsTabContainer.find('#diagnosticsHistoryMenu').addClass('d-none');
        this.diagnosticsTabContainer.find('#selectedDiagnosticButton').removeClass('is-open');
    }

    private filterDiagnosticHistory(): void {
        const searchValue = String(
            this.diagnosticsTabContainer.find('#diagnosticSearchInput').val() ?? ''
        ).trim().toLowerCase();

        this.getDiagnosticHistoryItems().each((_index, element) => {
            const item = $(element);
            const id = this.getDiagnosticId(item);
            const date = item.find('.diagnostics-history-item-date').text();
            const searchableText = `${date} ${id}`.toLowerCase();

            item.toggle(!searchValue || searchableText.includes(searchValue));
        });
    }

    private selectDiagnostic(item: JQuery): void {
        const diagnosticId = this.getDiagnosticId(item);

        if (!diagnosticId) {
            return;
        }

        if (item.hasClass('is-active')) {
            this.closeDiagnosticHistory();
            return;
        }

        this.navigateToDiagnostic(diagnosticId);
    }

    private navigateRelativeDiagnostic(offset: number): void {
        const items = this.getDiagnosticHistoryItems();
        const activeItem = items.filter('.is-active').first();

        if (!activeItem.length) {
            return;
        }

        const currentIndex = items.index(activeItem);
        const newIndex = currentIndex + offset;

        if (newIndex < 0 || newIndex >= items.length) {
            return;
        }

        const diagnosticId = this.getDiagnosticId(items.eq(newIndex));

        if (diagnosticId) {
            this.navigateToDiagnostic(diagnosticId);
        }
    }

    private navigateToDiagnostic(diagnosticId: string): void {
        const url = new URL(window.location.href);

        url.searchParams.set(this.diagnosticIdQueryParameter, diagnosticId);

        window.location.assign(url.toString());
    }

    private updateSelectedDiagnosticText(): void {
        const activeItem = this.getDiagnosticHistoryItems().filter('.is-active').first();

        if (!activeItem.length) {
            return;
        }

        const dateElement = activeItem.find('.diagnostics-history-item-date').clone();
        const badgeText = dateElement.find('.diagnostics-history-item-badge').text().trim();

        dateElement.find('.diagnostics-history-item-badge').remove();

        const dateText = dateElement.text().replace(/\s+/g, ' ').trim();
        const displayText = badgeText
            ? `${dateText} (${badgeText})`
            : dateText;

        this.setText('#selectedDiagnosticText', displayText);
    }

    private updateDiagnosticNavigation(): void {
        const items = this.getDiagnosticHistoryItems();
        const activeItem = items.filter('.is-active').first();

        if (!activeItem.length) {
            this.diagnosticsTabContainer.find('#previousDiagnosticButton').prop('disabled', true);
            this.diagnosticsTabContainer.find('#nextDiagnosticButton').prop('disabled', true);
            return;
        }

        const currentIndex = items.index(activeItem);

        /*
         * List is sorted newest -> oldest:
         * Previous = older record
         * Next     = newer record
         */
        this.diagnosticsTabContainer
            .find('#previousDiagnosticButton')
            .prop('disabled', currentIndex >= items.length - 1);

        this.diagnosticsTabContainer
            .find('#nextDiagnosticButton')
            .prop('disabled', currentIndex <= 0);
    }

    private getDiagnosticHistoryItems(): JQuery {
        return this.diagnosticsTabContainer
            .find('#diagnosticHistoryList .diagnostics-history-item');
    }

    private getDiagnosticId(item: JQuery): string {
        return String(item.data('id') ?? '');
    }

    // -------------------------------------------------------------------------
    // Header
    // -------------------------------------------------------------------------

    private renderHeader(): void {
        this.setText('#machineName', this.serverDiagnostics.MachineName);
        this.setText('#collectedAt', this.formatDateTime(this.serverDiagnostics.CollectedAtUtc));
    }

    // -------------------------------------------------------------------------
    // CPU
    // -------------------------------------------------------------------------

    private renderCpu(): void {
        const cpu = this.serverDiagnostics.Cpu;
        const status = this.getUsageStatus(cpu.UsagePercent);

        this.setText('#cpuUsage', this.formatPercentage(cpu.UsagePercent));
        this.setText('#cpuName', cpu.Name ?? 'Unknown processor');
        this.setText(
            '#cpuCores',
            `${cpu.PhysicalCores} cores · ${cpu.LogicalProcessors} logical processors`
        );

        this.setProgress('#cpuProgress', cpu.UsagePercent ?? 0, status);
        this.setStatus('#cpuStatus', status);
        this.setStatus('#cpuIcon', status);
    }

    // -------------------------------------------------------------------------
    // Memory
    // -------------------------------------------------------------------------

    private renderMemory(): void {
        const memory = this.serverDiagnostics.Memory;
        const status = this.getUsageStatus(memory.UsagePercent);

        this.setText('#memoryUsage', this.formatPercentage(memory.UsagePercent));
        this.setText(
            '#memoryDescription',
            `${this.formatBytes(memory.UsedBytes)} of ${this.formatBytes(memory.UsableBytes)} used`
        );
        this.setText(
            '#memoryAvailable',
            `${this.formatBytes(memory.AvailableBytes)} available`
        );

        this.setProgress('#memoryProgress', memory.UsagePercent, status);
        this.setStatus('#memoryStatus', status);
        this.setStatus('#memoryIcon', status);
    }

    // -------------------------------------------------------------------------
    // Storage
    // -------------------------------------------------------------------------

    private renderStorage(): void {
        const drives = this.serverDiagnostics.Drives ?? [];
        const container = this.diagnosticsTabContainer.find('#drivesContainer');

        container.empty();

        let totalBytes = 0;
        let usedBytes = 0;

        const statuses: DiagnosticsStatus[] = [];

        drives.forEach(drive => {
            totalBytes += drive.TotalBytes;
            usedBytes += drive.UsedBytes;

            const status = this.getUsageStatus(drive.UsagePercent);

            statuses.push(status);
            container.append(this.createDriveHtml(drive, status));
        });

        const usagePercentage = totalBytes > 0
            ? usedBytes / totalBytes * 100
            : 0;

        /*
         * The overall storage status uses the worst drive rather than the
         * combined usage. A nearly full C:\ should therefore still be critical.
         */
        const storageStatus = this.getWorstStatus(statuses);

        this.setText('#diskSummary', this.formatPercentage(usagePercentage));
        this.setText(
            '#diskDescription',
            `${this.formatBytes(usedBytes)} of ${this.formatBytes(totalBytes)} used`
        );

        this.setProgress('#storageProgress', usagePercentage, storageStatus);
        this.setStatus('#diskStatus', storageStatus);
        this.setStatus('#storageIcon', storageStatus);
    }

    private createDriveHtml(
        drive: DriveDiagnostics,
        status: DiagnosticsStatus
    ): string {
        const volumeLabel = drive.VolumeLabel
            ? `<span class="diagnostics-drive-label">${this.escapeHtml(drive.VolumeLabel)}</span>`
            : '';

        return `
            <div class="col-xl-6 col-lg-6">
                <div class="diagnostics-drive-card ${this.getStatusClass(status)}">
                    <div class="diagnostics-drive-header">
                        <div class="diagnostics-drive-title-container">
                            <div class="diagnostics-drive-icon">
                                <i class="fa-solid fa-hard-drive"></i>
                            </div>
                            <span class="diagnostics-drive-name">${this.escapeHtml(drive.Name)}</span>
                            ${volumeLabel}
                        </div>

                        <span class="diagnostics-drive-percentage">
                            ${this.formatPercentage(drive.UsagePercent)}
                        </span>
                    </div>

                    <div class="progress diagnostics-drive-progress">
                        <div class="progress-bar ${this.getProgressClass(status)}"
                             role="progressbar"
                             style="width: ${this.clamp(drive.UsagePercent)}%">
                        </div>
                    </div>

                    <div class="diagnostics-drive-footer">
                        <span>${this.formatBytes(drive.UsedBytes)} used</span>
                        <span>${this.formatBytes(drive.FreeBytes)} free</span>
                    </div>
                </div>
            </div>
        `;
    }

    // -------------------------------------------------------------------------
    // Databases
    // -------------------------------------------------------------------------

    private renderDatabases(): void {
        const databases = this.sqlDiagnostics.Databases ?? [];
        const container = this.diagnosticsTabContainer.find('#databasesContainer');

        container.empty();

        const statuses: DiagnosticsStatus[] = [];

        databases.forEach(database => {
            const healthStatus = this.getDatabaseHealthStatus(database);
            const storageStatus = this.getDatabaseStorageStatus(database);

            const combinedStatus = storageStatus === 'unknown'
                ? healthStatus
                : this.getWorstStatus([healthStatus, storageStatus]);

            statuses.push(combinedStatus);
            container.append(
                this.createDatabaseHtml(database, healthStatus, storageStatus)
            );
        });

        const onlineCount = databases.filter(
            database => database.State?.toUpperCase() === 'ONLINE'
        ).length;

        this.setText('#databaseCount', databases.length.toString());
        this.setText(
            '#databaseDescription',
            `${onlineCount} of ${databases.length} online`
        );

        const databaseStatus = this.getWorstStatus(statuses);

        this.setStatus('#databaseStatus', databaseStatus);
        this.setStatus('#databaseIcon', databaseStatus);

        const firstDatabase = databases[0];

        if (firstDatabase) {
            this.setText(
                '#sqlServerDescription',
                `${firstDatabase.Edition} · ${firstDatabase.ProductVersion}`
            );
        }
    }

    private createDatabaseHtml(
        database: DatabaseDiagnostics,
        healthStatus: DiagnosticsStatus,
        storageStatus: DiagnosticsStatus
    ): string {
        const maximum =
            database.EffectiveMaxDataBytes ??
            database.ConfiguredMaxDataBytes ??
            database.EditionMaxDataBytes;

        const usage = database.DataUsagePercent;

        const storageUsageHtml = usage !== undefined && usage !== null
            ? `
                <div class="diagnostics-database-progress-container">
                    <div class="progress diagnostics-database-progress">
                        <div class="progress-bar ${this.getProgressClass(storageStatus)}"
                             style="width: ${this.clamp(usage)}%">
                        </div>
                    </div>
                    <span class="diagnostics-database-progress-value">
                        ${this.formatPercentage(usage)}
                    </span>
                </div>
            `
            : `
                <span class="diagnostics-database-no-limit">
                    No configured limit
                </span>
            `;

        const maximumText = maximum !== undefined && maximum !== null
            ? `
                <span class="diagnostics-database-size-primary">
                    ${this.formatBytes(database.DataAllocatedBytes)}
                </span>
                <span class="diagnostics-database-size-secondary">
                    of ${this.formatBytes(maximum)}
                </span>
            `
            : `
                <span class="diagnostics-database-size-primary">
                    ${this.formatBytes(database.DataAllocatedBytes)}
                </span>
                <span class="diagnostics-database-size-secondary">
                    allocated
                </span>
            `;

        return `
            <tr>
                <td>
                    <div class="diagnostics-database-name-container">
                        <i class="fa-solid fa-database diagnostics-database-icon"></i>
                        <span class="diagnostics-database-name">
                            ${this.escapeHtml(database.DatabaseName)}
                        </span>
                    </div>
                </td>

                <td>
                    <div class="diagnostics-database-status">
                        <span class="diagnostics-database-status-dot ${this.getStatusClass(healthStatus)}"></span>
                        <span>${this.formatDatabaseState(database.State)}</span>
                    </div>
                </td>

                <td>${this.escapeHtml(database.RecoveryModel)}</td>
                <td>${database.CompatibilityLevel}</td>
                <td>${storageUsageHtml}</td>
                <td>${maximumText}</td>
            </tr>
        `;
    }

    private getDatabaseHealthStatus(database: DatabaseDiagnostics): DiagnosticsStatus {
        if (!database.State) {
            return 'unknown';
        }

        return database.State.toUpperCase() === 'ONLINE'
            ? 'healthy'
            : 'critical';
    }

    private getDatabaseStorageStatus(database: DatabaseDiagnostics): DiagnosticsStatus {
        if (
            database.DataUsagePercent === undefined ||
            database.DataUsagePercent === null
        ) {
            return 'unknown';
        }

        return this.getUsageStatus(database.DataUsagePercent);
    }

    private formatDatabaseState(state?: string): string {
        if (!state) {
            return 'Unknown';
        }

        const lower = state.toLowerCase();

        return lower.charAt(0).toUpperCase() + lower.slice(1);
    }

    // -------------------------------------------------------------------------
    // System information
    // -------------------------------------------------------------------------

    private renderSystemInformation(): void {
        const server = this.serverDiagnostics;
        const operatingSystem = server.OperatingSystem;

        this.setText('#systemMachine', server.MachineName);
        this.setText('#systemManufacturer', server.Manufacturer ?? '-');
        this.setText('#systemModel', server.Model ?? '-');

        const osText = [
            operatingSystem.Name,
            operatingSystem.Version
        ]
            .filter(value => !!value)
            .join(' · ');

        this.setText('#systemOs', osText || '-');
        this.setText('#systemArchitecture', operatingSystem.Architecture ?? '-');
        this.setText('#systemUptime', this.formatUptime(operatingSystem.UptimeSeconds));

        this.setText(
            '#systemLastBoot',
            operatingSystem.LastBootTime
                ? `Last boot: ${this.formatDateTime(operatingSystem.LastBootTime)}`
                : ''
        );
    }

    // -------------------------------------------------------------------------
    // Overall status
    // -------------------------------------------------------------------------

    private renderOverallStatus(): void {
        const statuses: DiagnosticsStatus[] = [
            this.getUsageStatus(this.serverDiagnostics.Cpu.UsagePercent),
            this.getUsageStatus(this.serverDiagnostics.Memory.UsagePercent)
        ];

        (this.serverDiagnostics.Drives ?? []).forEach(drive => {
            statuses.push(this.getUsageStatus(drive.UsagePercent));
        });

        (this.sqlDiagnostics.Databases ?? []).forEach(database => {
            const healthStatus = this.getDatabaseHealthStatus(database);
            const storageStatus = this.getDatabaseStorageStatus(database);

            const status = storageStatus === 'unknown'
                ? healthStatus
                : this.getWorstStatus([healthStatus, storageStatus]);

            if (status !== 'unknown') {
                statuses.push(status);
            }
        });

        const status = this.getWorstStatus(statuses);
        const element = this.diagnosticsTabContainer.find('#overallStatus');
        const icon = element.find('i');
        const text = element.find('span');

        element
            .removeClass('status-healthy status-warning status-critical status-unknown')
            .addClass(this.getStatusClass(status));

        icon.removeClass(
            'fa-circle-check fa-triangle-exclamation fa-circle-question'
        );

        switch (status) {
            case 'healthy':
                icon.addClass('fa-circle-check');
                text.text('Healthy');
                break;

            case 'warning':
                icon.addClass('fa-triangle-exclamation');
                text.text('Warning');
                break;

            case 'critical':
                icon.addClass('fa-triangle-exclamation');
                text.text('Critical');
                break;

            default:
                icon.addClass('fa-circle-question');
                text.text('Unknown');
                break;
        }
    }

    // -------------------------------------------------------------------------
    // Technical details
    // -------------------------------------------------------------------------

    private renderTechnicalDetails(): void {
        this.diagnosticsTabContainer
            .find('#serverJsonOutput')
            .text(JSON.stringify(this.serverDiagnostics, null, 2));

        this.diagnosticsTabContainer
            .find('#sqlJsonOutput')
            .text(JSON.stringify(this.sqlDiagnostics, null, 2));
    }

    private toggleTechnicalDetails(): void {
        const container = this.diagnosticsTabContainer.find('#technicalDetails');
        const button = this.diagnosticsTabContainer.find('#toggleTechnicalDetails span');

        container.toggleClass('d-none');

        button.text(
            container.hasClass('d-none')
                ? 'Technical details'
                : 'Hide technical details'
        );
    }

    // -------------------------------------------------------------------------
    // Status helpers
    // -------------------------------------------------------------------------

    private getUsageStatus(percentage?: number | null): DiagnosticsStatus {
        if (percentage === undefined || percentage === null) {
            return 'unknown';
        }

        if (percentage >= 90) {
            return 'critical';
        }

        if (percentage >= 80) {
            return 'warning';
        }

        return 'healthy';
    }

    private getWorstStatus(statuses: DiagnosticsStatus[]): DiagnosticsStatus {
        if (!statuses.length) {
            return 'unknown';
        }

        if (statuses.includes('critical')) {
            return 'critical';
        }

        if (statuses.includes('warning')) {
            return 'warning';
        }

        if (statuses.every(status => status === 'unknown')) {
            return 'unknown';
        }

        return 'healthy';
    }

    private setStatus(selector: string, status: DiagnosticsStatus): void {
        this.diagnosticsTabContainer
            .find(selector)
            .removeClass('status-healthy status-warning status-critical status-unknown')
            .addClass(this.getStatusClass(status));
    }

    private getStatusClass(status: DiagnosticsStatus): string {
        return `status-${status}`;
    }

    private setProgress(
        selector: string,
        percentage: number,
        status: DiagnosticsStatus
    ): void {
        this.diagnosticsTabContainer
            .find(selector)
            .css('width', `${this.clamp(percentage)}%`)
            .removeClass('bg-success bg-warning bg-danger bg-secondary')
            .addClass(this.getProgressClass(status));
    }

    private getProgressClass(status: DiagnosticsStatus): string {
        switch (status) {
            case 'critical':
                return 'bg-danger';

            case 'warning':
                return 'bg-warning';

            case 'healthy':
                return 'bg-success';

            default:
                return 'bg-secondary';
        }
    }

    // -------------------------------------------------------------------------
    // Formatting
    // -------------------------------------------------------------------------

    private formatBytes(bytes?: number | null): string {
        if (bytes === undefined || bytes === null) {
            return '-';
        }

        if (bytes === 0) {
            return '0 B';
        }

        const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
        const index = Math.floor(Math.log(bytes) / Math.log(1024));
        const safeIndex = Math.min(index, units.length - 1);
        const value = bytes / Math.pow(1024, safeIndex);
        const decimals = safeIndex >= 3 ? 1 : 0;

        return `${value.toFixed(decimals)} ${units[safeIndex]}`;
    }

    private formatPercentage(percentage?: number | null): string {
        if (percentage === undefined || percentage === null) {
            return '-';
        }

        return `${percentage.toFixed(1)}%`;
    }

    private formatUptime(seconds?: number | null): string {
        if (seconds === undefined || seconds === null) {
            return '-';
        }

        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);

        if (days > 0) {
            return `${days}d ${hours}h ${minutes}m`;
        }

        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }

        return `${minutes}m`;
    }

    private formatDateTime(value?: string): string {
        if (!value) {
            return '-';
        }

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return '-';
        }

        return date.toLocaleString(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short'
        });
    }

    private clamp(value: number): number {
        return Math.max(0, Math.min(100, value));
    }

    private setText(selector: string, value: string): void {
        this.diagnosticsTabContainer.find(selector).text(value);
    }

    private escapeHtml(value?: string | null): string {
        if (!value) {
            return '';
        }

        return $('<div>').text(value).html();
    }
}